import { buildDashboardRoute } from "./dashboardRouteUtils";

const MIN_PRODUCT_SCORE = 1;
const PAIR_Q_THRESHOLD = 0.3;
const PAIR_A_THRESHOLD = 0.35;
const STRONG_SIGNAL_THRESHOLD = 0.5;
const PAIR_Q_SCORE_FLOOR = 1;
const PAIR_A_SCORE_FLOOR = 1;
const STRONG_Q_SCORE_FLOOR = 2;
const STRONG_A_SCORE_FLOOR = 2;

export const PRODUCT_KEYS = {
  CO_TRADER_FX: "co_trader_fx",
  CO_TRADER_STOCKS: "co_trader_stocks",
  DASHBOARD_MACRO: "dashboard_macro",
};

const PRODUCT_META = {
  [PRODUCT_KEYS.CO_TRADER_FX]: {
    key: PRODUCT_KEYS.CO_TRADER_FX,
    name: "CoTrader",
    route: "/co-trader",
  },
  [PRODUCT_KEYS.CO_TRADER_STOCKS]: {
    key: PRODUCT_KEYS.CO_TRADER_STOCKS,
    name: "CoTrader",
    route: "/co-trader",
  },
  [PRODUCT_KEYS.DASHBOARD_MACRO]: {
    key: PRODUCT_KEYS.DASHBOARD_MACRO,
    name: "Macro Monitor",
    route: "/dashboard",
  },
};

const FX_CODES = new Set([
  "USD",
  "EUR",
  "JPY",
  "GBP",
  "CHF",
  "CAD",
  "AUD",
  "NZD",
  "SEK",
  "NOK",
  "DKK",
  "CNY",
  "CNH",
  "INR",
  "KRW",
  "SGD",
  "HKD",
  "MXN",
  "BRL",
  "TRY",
  "ZAR",
]);

const FX_TERMS = [
  "fx",
  "forex",
  "currency",
  "currencies",
  "spot",
  "forward",
  "overnight",
  "yield",
  "cross",
  "pip",
  "dollar",
  "euro",
  "yen",
  "sterling",
];

const STOCK_TERMS = [
  "stock",
  "stocks",
  "equity",
  "equities",
  "shares",
  "earnings",
  "valuation",
  "market cap",
  "guidance",
  "company",
  "companies",
  "ticker",
  "eps",
];

const MACRO_TERMS = [
  "macro",
  "inflation",
  "cpi",
  "pce",
  "nfp",
  "payroll",
  "fomc",
  "fed",
  "ecb",
  "boj",
  "boe",
  "central bank",
  "gdp",
  "pmi",
  "policy rate",
  "interest rate",
  "unemployment",
  "treasury",
  "recession",
];

const norm = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const countKeywordHits = (text, keywords) =>
  keywords.reduce((sum, keyword) => (text.includes(keyword) ? sum + 1 : sum), 0);

const getFxPair = (value) => {
  const upper = String(value || "").toUpperCase();
  const pairRegex = /\b([A-Z]{3})[\s/\-]?([A-Z]{3})\b/g;
  let match = pairRegex.exec(upper);
  while (match) {
    const a = match[1];
    const b = match[2];
    if (a !== b && FX_CODES.has(a) && FX_CODES.has(b)) return `${a}${b}`;
    match = pairRegex.exec(upper);
  }
  return null;
};

const getStockSymbol = (value) => {
  const text = String(value || "");
  const eqMatch = text.match(/\bEQ-[A-Za-z0-9._-]+\b/);
  if (eqMatch?.[0]) return eqMatch[0];

  const tickerMatch = text.match(/\$([A-Z]{1,5})\b/);
  if (tickerMatch?.[1]) return tickerMatch[1];

  const namedTickerMatch = text.match(/\bticker\s+([A-Z]{1,5})\b/i);
  if (namedTickerMatch?.[1]) return namedTickerMatch[1].toUpperCase();

  return null;
};

const getProductSignals = (value) => {
  const text = norm(value);
  if (!text) return [];

  const fxPair = getFxPair(value);
  const stockSymbol = getStockSymbol(value);

  const scores = {
    [PRODUCT_KEYS.CO_TRADER_FX]: countKeywordHits(text, FX_TERMS),
    [PRODUCT_KEYS.CO_TRADER_STOCKS]: countKeywordHits(text, STOCK_TERMS),
    [PRODUCT_KEYS.DASHBOARD_MACRO]: countKeywordHits(text, MACRO_TERMS),
  };

  if (fxPair) scores[PRODUCT_KEYS.CO_TRADER_FX] += 3;
  if (stockSymbol) scores[PRODUCT_KEYS.CO_TRADER_STOCKS] += 3;

  const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);

  return Object.entries(scores)
    .filter(([, score]) => score >= MIN_PRODUCT_SCORE)
    .map(([productKey, score]) => ({
      productKey,
      score,
      confidence: score / (totalScore + 1),
      entities: {
        fxPair,
        stockSymbol,
      },
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return b.confidence - a.confidence;
    });
};

const getMacroRouteFromTabs = (tabs = []) => {
  const monitorsTab = tabs.find(
    (tab) =>
      tab?.path === "/dashboard" ||
      tab?.name === "Agents" ||
      tab?.name === "Research Agents" ||
      tab?.name === "Monitors",
  );
  if (!monitorsTab || !Array.isArray(monitorsTab.subTabs)) {
    return {
      route: "/dashboard",
      hasAccess: false,
      dashboardName: null,
    };
  }

  const macroSubTab = monitorsTab.subTabs.find((subTab) => {
    const hay = `${subTab?.name || ""} ${subTab?.dashboardName || ""}`.toLowerCase();
    return hay.includes("macro") || hay.includes("frontier");
  });

  if (!macroSubTab?.dashboardName) {
    return {
      route: "/dashboard",
      hasAccess: false,
      dashboardName: null,
    };
  }

  return {
    route: macroSubTab.path || buildDashboardRoute(macroSubTab.dashboardName),
    hasAccess: true,
    dashboardName: macroSubTab.dashboardName,
  };
};

const hasPathAccess = (tabs, path) =>
  Array.isArray(tabs) &&
  tabs.some((tab) => String(tab?.path || "").toLowerCase() === path.toLowerCase());

const resolveProductRoute = (productKey, tabs = []) => {
  if (productKey === PRODUCT_KEYS.DASHBOARD_MACRO) return getMacroRouteFromTabs(tabs);

  const meta = PRODUCT_META[productKey];
  if (!meta) return { route: "/", hasAccess: false, dashboardName: null };
  return {
    route: meta.route,
    hasAccess: hasPathAccess(tabs, meta.route),
    dashboardName: null,
  };
};

const buildSuggestion = (signal, tabs = []) => {
  const meta = PRODUCT_META[signal.productKey];
  if (!meta) return null;

  const routeInfo = resolveProductRoute(signal.productKey, tabs);
  return {
    productKey: signal.productKey,
    productName: meta.name,
    ctaText: `Want to continue in ${meta.name}?`,
    route: routeInfo.route,
    isLocked: !routeInfo.hasAccess,
    entities: {
      ...signal.entities,
      dashboardName: routeInfo.dashboardName,
    },
  };
};

export const buildProductSuggestionsForPair = ({
  questionText,
  answerText,
  tabs = [],
}) => {
  const questionSignals = getProductSignals(questionText);
  const answerSignals = getProductSignals(answerText);

  const questionByProduct = new Map(
    questionSignals.map((signal) => [signal.productKey, signal]),
  );
  const answerByProduct = new Map(
    answerSignals.map((signal) => [signal.productKey, signal]),
  );

  const candidateKeys = new Set([
    ...questionByProduct.keys(),
    ...answerByProduct.keys(),
  ]);

  const suggestions = [...candidateKeys]
    .map((productKey) => {
      const q = questionByProduct.get(productKey);
      const a = answerByProduct.get(productKey);

      const hasModeratePair =
        q &&
        a &&
        (q.confidence >= PAIR_Q_THRESHOLD || q.score >= PAIR_Q_SCORE_FLOOR) &&
        (a.confidence >= PAIR_A_THRESHOLD || a.score >= PAIR_A_SCORE_FLOOR);

      const hasStrongAnswer =
        a &&
        (a.confidence >= STRONG_SIGNAL_THRESHOLD ||
          a.score >= STRONG_A_SCORE_FLOOR);

      const hasStrongQuestion =
        q &&
        (q.confidence >= STRONG_SIGNAL_THRESHOLD ||
          q.score >= STRONG_Q_SCORE_FLOOR);

      if (!hasModeratePair && !hasStrongAnswer && !hasStrongQuestion) {
        return null;
      }

      const dominantSignal = a?.score >= (q?.score ?? 0) ? a || q : q || a;

      return {
        suggestion: buildSuggestion(dominantSignal, tabs),
        rankScore:
          (a?.score || 0) * 2 +
          (q?.score || 0) +
          (a?.confidence || 0) +
          (q?.confidence || 0),
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.rankScore - left.rankScore)
    .map((entry) => entry.suggestion)
    .filter(Boolean);

  return suggestions;
};

export const buildProductSuggestionForPair = (args) =>
  buildProductSuggestionsForPair(args)[0] || null;
